import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "../firebaseConfig";
import { fetchAdminOrders, updateOrderStatus } from "../utils/api";
import toast from "react-hot-toast";

const STATUSES = [
  "pending_payment", "pending_booking", "paid", "booked",
  "shipped", "delivered", "cancelled", "refunded",
];

const STATUS_STYLES = {
  paid: "bg-green-100 text-green-700",
  booked: "bg-blue-100 text-blue-700",
  shipped: "bg-indigo-100 text-indigo-700",
  delivered: "bg-emerald-100 text-emerald-700",
  cancelled: "bg-red-100 text-red-700",
  refunded: "bg-orange-100 text-orange-700",
  pending_payment: "bg-yellow-100 text-yellow-700",
  pending_booking: "bg-yellow-100 text-yellow-700",
};

function formatDate(ms) {
  if (!ms) return "—";
  try {
    return new Date(ms).toLocaleString();
  } catch {
    return "—";
  }
}

export default function Admin() {
  const [user, setUser] = useState(null);
  const [authReady, setAuthReady] = useState(false);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setUser(u);
      setAuthReady(true);
    });
    return () => unsub();
  }, []);

  const loadOrders = useCallback(async () => {
    if (!auth.currentUser) return;
    setLoading(true);
    setError("");
    try {
      const token = await auth.currentUser.getIdToken();
      const data = await fetchAdminOrders(token);
      setOrders(data);
    } catch (e) {
      setError(e.message || "Failed to load orders");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authReady && user) loadOrders();
  }, [authReady, user, loadOrders]);

  const handleStatusChange = async (orderId, newStatus) => {
    try {
      const token = await auth.currentUser.getIdToken();
      await updateOrderStatus(token, orderId, newStatus);
      setOrders((prev) =>
        prev.map((o) =>
          o.orderId === orderId ? { ...o, orderStatus: newStatus } : o
        )
      );
      toast.success("Order updated");
    } catch (e) {
      toast.error(e.message || "Update failed");
    }
  };

  if (!authReady) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center text-gray-500">
        Loading…
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center gap-4 text-center px-6">
        <h1 className="text-2xl font-bold text-gray-800">Admin Access</h1>
        <p className="text-gray-600">Please log in with an admin account.</p>
        <Link
          to="/login"
          className="bg-brand text-black font-semibold px-6 py-2 rounded-lg"
        >
          Go to Login
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Orders</h1>
          <p className="text-sm text-gray-500">Signed in as {user.email}</p>
        </div>
        <button
          onClick={loadOrders}
          disabled={loading}
          className="bg-brand text-black font-semibold px-4 py-2 rounded-lg disabled:opacity-60"
        >
          {loading ? "Refreshing…" : "Refresh"}
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4 mb-6">
          {error === "Not authorized"
            ? "This account isn't on the admin allowlist. Ask to have your email added to ADMIN_EMAILS."
            : error}
        </div>
      )}

      {!error && orders.length === 0 && !loading && (
        <p className="text-gray-500">No orders yet.</p>
      )}

      {orders.length > 0 && (
        <div className="overflow-x-auto rounded-lg border border-gray-200">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-600 text-left">
              <tr>
                <th className="p-3">Date</th>
                <th className="p-3">Customer</th>
                <th className="p-3">Items</th>
                <th className="p-3">Total</th>
                <th className="p-3">Payment</th>
                <th className="p-3">Tracking</th>
                <th className="p-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => (
                <tr key={o.orderId} className="border-t border-gray-100 align-top">
                  <td className="p-3 whitespace-nowrap text-gray-500">
                    {formatDate(o.createdAt)}
                  </td>
                  <td className="p-3">
                    <div className="font-medium text-gray-800">
                      {o.customer?.name}
                    </div>
                    <div className="text-gray-500">{o.customer?.phone}</div>
                    <div className="text-gray-400 text-xs">
                      {o.customer?.city}
                    </div>
                  </td>
                  <td className="p-3 text-gray-600">
                    {(o.items || []).map((it, i) => (
                      <div key={i}>
                        {it.quantity}× {it.title}
                      </div>
                    ))}
                  </td>
                  <td className="p-3 whitespace-nowrap font-medium">
                    {o.currency} {Number(o.totalAmount || 0).toFixed(2)}
                  </td>
                  <td className="p-3">
                    <div className="capitalize">{o.paymentMethod}</div>
                    <div
                      className={`inline-block mt-1 px-2 py-0.5 rounded text-xs ${
                        o.paymentStatus === "paid"
                          ? "bg-green-100 text-green-700"
                          : "bg-gray-100 text-gray-600"
                      }`}
                    >
                      {o.paymentStatus}
                    </div>
                  </td>
                  <td className="p-3 text-gray-600">
                    {o.trackingNumber || "—"}
                  </td>
                  <td className="p-3">
                    <span
                      className={`block mb-1 px-2 py-0.5 rounded text-xs w-fit ${
                        STATUS_STYLES[o.orderStatus] || "bg-gray-100 text-gray-600"
                      }`}
                    >
                      {o.orderStatus}
                    </span>
                    <select
                      value={o.orderStatus}
                      onChange={(e) => handleStatusChange(o.orderId, e.target.value)}
                      className="border rounded p-1 text-xs"
                    >
                      {STATUSES.map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
